import { useEffect, useRef, useState } from 'react';
import { getCurrentUser, updatePassword } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import { SessionManagerService } from 'session-manager/services/SessionManagerService';

import { Button } from '@client:common/button';
import { Notification } from '@client:common/overlay/notification';
import { GlobalStore } from '@client:common/global-store';
import {
  Form,
  FormControl,
  FormGroup,
  PasswordFormField
} from '@client:common/form';
import { Validators } from '@client:common/form/validation';
import { ProgressIndicator } from '@client:common/overlay/progress-indicator';
import './ResetPassword.dark.scss';
import './ResetPassword.light.scss';

export interface UpdateAccountDto {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  organization: string;
}

export const ResetPassword = () => {
  const sessionManagerService = SessionManagerService.getInstance();
  const globalStore = GlobalStore.getInstance();
  const navigate = useNavigate();

  const passwordControlRef = useRef(
    new FormControl('', [Validators.checkPassword()])
  );
  const newPasswordControlRef = useRef(
    new FormControl('', [Validators.checkPassword()])
  );
  const formGroupRef = useRef(
    new FormGroup({
      password: passwordControlRef.current,
      newPassword: newPasswordControlRef.current
    })
  );

  useEffect(() => {
    const subscription = formGroupRef.current.validityChanges().subscribe({
      next: setIsFormValid
    });
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const [isFormValid, setIsFormValid] = useState(
    formGroupRef.current.isValid()
  );

  async function updateUserDataWithPassword() {
    ProgressIndicator.show();

    try {
      // Get the current authenticated user - this is just to verify the user is authenticated
      await getCurrentUser();

      // Change the password using the new API
      await updatePassword({
        oldPassword: passwordControlRef.current.getValue(),
        newPassword: newPasswordControlRef.current.getValue()
      });

      // Sign out after successful password change
      try {
        await sessionManagerService.signOut();
        globalStore.update({
          authenticatedAccount: null
        });
        navigate('/');
      } catch {
        Notification.open('Logging out failed, please try again');
      }
    } catch {
      Notification.open('Password update failed, please try again');
    } finally {
      ProgressIndicator.hide();
    }
  }

  const onSaveChanges = () => {
    updateUserDataWithPassword();
  };

  return (
    <div className='login-form-container'>
      <h2>Reset Password</h2>
      <Form
        className='password_collector'
        formGroup={formGroupRef.current}>
        <PasswordFormField
          label='Old Password'
          formControl={formGroupRef.current.findControl('password')}
        />
        <PasswordFormField
          label='New Password'
          formControl={formGroupRef.current.findControl('newPassword')}
        />
        <div className='account-form-button-wrapper'>
          <Button
            style='raised'
            color='primary'
            disabled={!isFormValid || formGroupRef.current.isPristine()}
            onClick={() => onSaveChanges()}
          >
            SAVE CHANGES
          </Button>
        </div>
      </Form>
    </div>
  );
};
